import React from 'react'

import { Skeleton } from 'components'
import { getLabel } from 'helpers/language'

import type { TicketLabelProps } from './model'

const TicketLabel = (props: TicketLabelProps): JSX.Element => {
  const {
    isLoading = true,
    isRow = false,
    isShowSkeletonTitle = false,
    skeletonAlign = 'start',
    skeletonWidthTitle = '65px',
    skeletonWidthValue = '100%',
    title,
    value,
    valueElement,
    minWidthTitle = 135,
  } = props

  const base = 'f-ticket-label'
  const isAssigned = title === 'ticket:assignedToAndAssignedPhoneNumber.label'
  const isAddress = title === 'common:address.label'
  const isDescription = title === 'common:description.label'
  const customMinWidthTitle = { minWidth: minWidthTitle }

  const renderSkeletonTitle = (
    isAssigned: boolean,
    isShowSkeletonTitle: boolean,
    width: string,
    customMinWidthTitle: {
      minWidth: number
    },
  ) => {
    const skeleton = isAssigned ? (
      <div className={`${base}__assign-skeleton`}>
        <Skeleton.whiteSkeleton height='18px' width={width} />
        <Skeleton.whiteSkeleton height='18px' width='65px' />
      </div>
    ) : (
      <Skeleton.whiteSkeleton height='18px' width={width} />
    )

    return (
      isShowSkeletonTitle &&
      (isRow ? (
        <div style={{ ...customMinWidthTitle }}>{skeleton}</div>
      ) : (
        skeleton
      ))
    )
  }

  const renderSkeletonValue = (
    isAssigned: boolean,
    isAddress: boolean,
    isDescription: boolean,
    width: string,
  ) => {
    const skeleton = (
      <div style={{ width }}>
        <Skeleton.whiteSkeleton height='18px' width={width} />
      </div>
    )

    if (isAssigned) {
      return (
        <div className={`${base}__assign-skeleton`}>
          <Skeleton.whiteSkeleton height='18px' width={width} />
          <Skeleton.whiteSkeleton height='18px' width='75%' />
        </div>
      )
    }

    if (isAddress) {
      return (
        <div className={`${base}__address-skeleton`}>
          <Skeleton.whiteSkeleton height='18px' width={width} />
          <Skeleton.whiteSkeleton height='18px' width='85%' />
        </div>
      )
    }

    if (isDescription) {
      return (
        <div className={`${base}__description-skeleton`}>
          <Skeleton.whiteSkeleton height='18px' width={width} />
          <Skeleton.whiteSkeleton height='18px' width={width} />
          <Skeleton.whiteSkeleton height='18px' width='60%' />
        </div>
      )
    }

    if (isDescription) {
      return (
        <div className={`${base}__description-skeleton`}>
          <Skeleton.whiteSkeleton height='18px' width={width} />
          <Skeleton.whiteSkeleton height='18px' width={width} />
          <Skeleton.whiteSkeleton height='18px' width='60%' />
        </div>
      )
    }

    return skeleton
  }

  const renderContentTitle = (
    isAssigned: boolean,
    customMinWidthTitle: {
      minWidth: number
    },
  ) => {
    return isAssigned ? (
      <div className={`${base}__assign-title`}>
        <span>{getLabel(title)}</span>
        <span>{getLabel('ticket:delegate.label')}</span>
      </div>
    ) : (
      <span
        data-testid='text-title'
        className={`${base}__title`}
        style={{ ...customMinWidthTitle }}
      >
        {getLabel(title)}
      </span>
    )
  }

  return (
    <>
      {isLoading ? (
        <div
          data-testid='skeleton-ticket-label-loading'
          className={`text-align-${skeletonAlign} ${base}__skeleton`}
        >
          <div
            className={`${base}__body ${
              isRow ? `${base}__body--row` : `${base}__body--column`
            }`}
          >
            {renderSkeletonTitle(
              isAssigned,
              isShowSkeletonTitle,
              skeletonWidthTitle,
              customMinWidthTitle,
            )}
            {renderSkeletonValue(
              isAssigned,
              isAddress,
              isDescription,
              skeletonWidthValue,
            )}
          </div>
        </div>
      ) : (
        <div
          className={`${base}__body ${
            isRow ? `${base}__body--row` : `${base}__body--column`
          }`}
        >
          {renderContentTitle(isAssigned, customMinWidthTitle)}
          {valueElement || (
            <span className={`${base}__value`}>{value || '-'}</span>
          )}
        </div>
      )}
    </>
  )
}

export default TicketLabel
